import * as React from "react";
import { HelpCallout, HelpFaq, withBold } from "./HelpDialog";

// A short walk-through in the order a coach meets backgrounds, so each step reads as the next thing to do.
const STEPS = [
  {
    title: "Photos come ready to go",
    body: "When a slide's layout uses a background photo, it gets one from your library right away, so you never wait on image generation. You can keep it or swap it.",
  },
  {
    title: "Add a few more to choose from",
    body: "Use **Choose from library**, **Upload a photo** (JPG or PNG up to 10 MB), or **Generate more with AI**. Each one you add is saved to your library for any design.",
  },
  {
    title: "Pick the one players start with",
    body: "Click **Set as default** on the photo you like best. It's what players see first unless they change it.",
  },
  {
    title: "Decide how much players can change",
    body: "Players can choose from the photos on your design. Turn on **Your library** or **Their own photo** under the slide to give them more.",
  },
];

const FAQ = [
  {
    q: "How long does Generate more with AI take?",
    a: "Usually seconds, sometimes a few minutes when it's busy. New options appear on the slide as they're ready.",
  },
  {
    q: "Can I change a background with AI?",
    a: 'Yes. Choose **Refine with AI…** on any background and describe the change, like "make it warmer" or "more empty sky at the top". The original stays.',
  },
  {
    q: "What if a player uploads something off-brand?",
    a: "Turn off **Their own photo**. Players then choose from your library or the backgrounds on the design, and your options stay.",
  },
  {
    q: "Can players pick a different background for each slide of a carousel?",
    a: "No. A player's pick goes on every slide. Until they pick, carousel slides rotate through your photos, so the same picture doesn't repeat.",
  },
  {
    q: "What's the difference between Remove and Archive?",
    a: "**Remove** takes a background off one slide. **Archive**, on the Backgrounds tab, hides it from your library everywhere; designs already using it keep it.",
  },
];

export const HowBackgroundsWorkGuide: React.FC = () => (
  <>
    <ol className="space-y-4">
      {STEPS.map((step, i) => (
        <li key={step.title} className="flex gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-primary">
            {i + 1}
          </span>
          <div className="space-y-1 pt-0.5">
            <div className="text-base font-semibold text-slate-800">{step.title}</div>
            <p className="text-sm leading-normal text-slate-600">{withBold(step.body)}</p>
          </div>
        </li>
      ))}
    </ol>

    <HelpCallout>
      Posts already made never change. Changing a background rebuilds the image in a few seconds.
    </HelpCallout>

    <HelpFaq items={FAQ} />
  </>
);
